import { ref, watch } from 'vue'
import { usePageContext } from '../renderer/usePageContext'
import { useMainStore } from '../store'

export function useSearch(block, context, companyIds) {
  const searchQuery = ref({
    filter: block.searchTerm || '',
    employmentType: convertToArray(block.employmentType),
    discipline: convertToArray(block.discipline),
    education: convertToArray(block.education),
    workCategory: [],
    workExperience: [],
    region: [],
    geoLocation: {
      label: '',
      latitude: null,
      longitude: null,
      distance: 10,
    },
    fromStaffingAgency: 'INCLUDE',
    companyIds,
  })

  const store = useMainStore()

  function convertToArray(blokProperty) {
    if (blokProperty) {
      if (Array.isArray(blokProperty)) {
        return blokProperty.map((item) => item.key || item)
      }

      return blokProperty.replaceAll(', ', ',').split(',')
    }
    return []
  }

  // First, we update the search params based on the context
  let contextualSearch = false
  if (context.searchParams?.tagCategory) {
    contextualSearch = true
    switch (context.searchParams.tagCategory) {
      case 'REGION':
        searchQuery.value.region.push(context.searchParams.key)
        break
      case 'LOCATION':
        searchQuery.value.geoLocation = context.searchParams.geoLocation
        break
      case 'VACANCY_EDUCATION':
        searchQuery.value.education.push(context.searchParams.key)
        break
      case 'VACANCY_EMPLOYMENTTYPE':
        searchQuery.value.employmentType.push(context.searchParams.key)
        break
      case 'VACANCY_WORKCATEGORY':
        searchQuery.value.workCategory.push(context.searchParams.key)
        break
      case 'VACANCY_WORKEXPERIENCE':
        searchQuery.value.workExperience.push(context.searchParams.key)
        break
      case 'VACANCY_WORKFIELD':
        searchQuery.value.discipline.push(context.searchParams.key)
        break
      case 'COMPANY':
        searchQuery.value.filter = context.searchParams.label
        break
    }
  }

  // Then, we refresh the search params based on the URL
  const pageContext = usePageContext()
  const url = pageContext.isClientSide
    ? new URL(window.location.href)
    : new URL(`https://${pageContext.hostname}${pageContext.urlOriginal}`)
  const params = new URLSearchParams(url.search)

  if (params.get('filter')) {
    searchQuery.value.filter = params.get('filter')
  }
  if (params.get('latitude') && params.get('longitude')) {
    searchQuery.value.geoLocation.latitude = params.get('latitude')
    searchQuery.value.geoLocation.longitude = params.get('longitude')

    // Location only makes sense if also the coordinates are included
    if (params.get('location')) {
      searchQuery.value.geoLocation.label = params.get('location')
    }
  }
  if (params.get('distance')) {
    searchQuery.value.geoLocation.distance = params.get('distance')
  }
  if (params.get('fromStaffingAgency')) {
    searchQuery.value.fromStaffingAgency = params.get('fromStaffingAgency')
  }

  const arrayFields = [
    'employmentType',
    'discipline',
    'education',
    'workCategory',
    'workExperience',
    'region',
  ]
  arrayFields.forEach((name) => {
    if (params.get(name)) {
      searchQuery.value[name] = params.get(name).split(',')
    }
  })

  watch(
    searchQuery,
    (newSearch) => {
      const url = new URL(window.location.href)
      const params = new URLSearchParams(url.search)

      if (block.showSearchBar) {
        params.set('filter', newSearch.filter)

        if (block.includeLocationInSearch) {
          params.set('location', newSearch.geoLocation.label || '')
          params.set('latitude', newSearch.geoLocation.latitude || '')
          params.set('longitude', newSearch.geoLocation.longitude || '')
          params.set('distance', newSearch.geoLocation.distance || '')
        }
      }

      if (block.showSearchFilters) {
        params.set('employmentType', newSearch.employmentType)
        params.set('discipline', newSearch.discipline)
        params.set('education', newSearch.education)
        params.set('workCategory', newSearch.workCategory)
        params.set('workExperience', newSearch.workExperience)
        params.set('region', newSearch.region)
      }

      if (block.showStaffingAgencyFilter) {
        params.set('fromStaffingAgency', newSearch.fromStaffingAgency)
      }

      // Delete empty params
      const entries = Array.from(params.entries())
      entries.forEach(([key, value]) => {
        if (!value) {
          params.delete(key)
        }
      })

      let newURL
      if (contextualSearch) {
        contextualSearch = false
        const index = url.pathname.indexOf('/vacatures')
        params.delete('page')
        newURL = `${url.pathname.substring(0, index + '/vacatures'.length)}?${params.toString()}${url.hash}`
        store.clearContextualSearch()
      } else {
        newURL = `${url.pathname}?${params.toString()}${url.hash}`
      }
      // Update the URL without refreshing the page
      history.replaceState(null, '', newURL)
    },
    { deep: true },
  )

  return {
    searchQuery,
  }
}
